import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Category, MappingRule } from '@/types';
import { toast } from 'sonner';
import { AddMappingRuleDialog } from './AddMappingRuleDialog';
import { CategoryPathDisplay } from './CategoryPathDisplay';
import { MappingRulesTable } from './MappingRulesTable';

interface MappingRulesSectionProps {
  selectedCategory: Category | null;
  onUpdateCategory: (categoryId: string, updatedRules: MappingRule[]) => void;
}

export const MappingRulesSection: React.FC<MappingRulesSectionProps> = ({
  selectedCategory,
  onUpdateCategory,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<MappingRule | null>(null);
  const [rules, setRules] = useState<MappingRule[]>([]);

  // Update local rules when selected category changes
  useEffect(() => {
    if (selectedCategory) {
      console.log('Setting rules from category:', selectedCategory.mappingRules);
      setRules(selectedCategory.mappingRules || []);
    } else {
      setRules([]);
    }
  }, [selectedCategory]); // Watch the entire selectedCategory object for changes

  console.log('MappingRulesSection rendering with category:', selectedCategory);
  console.log('Current rules:', rules);

  if (!selectedCategory) {
    return (
      <div className="p-4">
        <div className="text-center text-gray-500">
          Select a category to manage mapping rules
        </div>
      </div>
    );
  }

  const handleAddRule = (ruleData: Omit<MappingRule, 'id'>) => {
    console.log('Adding new rule:', ruleData);
    
    const newRule: MappingRule = {
      id: `rule-${Date.now()}`,
      ...ruleData
    };

    const updatedRules = [...rules, newRule];
    console.log('Updated rules after adding:', updatedRules);
    
    setRules(updatedRules);
    onUpdateCategory(selectedCategory.id, updatedRules);
    toast.success(`New mapping rule added to ${selectedCategory.name}`);
  };

  const handleEditRule = (rule: MappingRule) => {
    console.log('Editing rule:', rule);
    setEditingRule(rule);
    setDialogOpen(true);
  };

  const handleDeleteRule = (ruleId: string) => {
    console.log('Deleting rule:', ruleId);
    
    const updatedRules = rules.filter((rule) => rule.id !== ruleId);
    console.log('Updated rules after deletion:', updatedRules);
    
    setRules(updatedRules);
    onUpdateCategory(selectedCategory.id, updatedRules);
    toast.success('Mapping rule deleted');
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <CategoryPathDisplay category={selectedCategory} />
        <Button 
          onClick={() => {
            setEditingRule(null);
            setDialogOpen(true);
          }} 
          className="bg-[#ff6d00] hover:bg-[#ff6d00]/90 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Rule
        </Button>
      </div>

      <MappingRulesTable
        rules={rules}
        onDeleteRule={handleDeleteRule}
        onEditRule={handleEditRule}
      />

      <AddMappingRuleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onAddRule={handleAddRule}
        editingRule={editingRule}
      />
    </div>
  );
};